import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { fetchProjectAnalysisReportHtml } from '@/services/project-analysis/fetchProjectAnalysisReportHtml';

type ReportHtmlPreviewProps = {
  htmlFile: File | null;
  serverReportId: string | null;
  token: string | null;
  refreshKey: number;
  /** Overrides default iframe height (e.g. `h-full` inside a fullscreen container) */
  frameClassName?: string;
};

export function ReportHtmlPreview({
  htmlFile,
  serverReportId,
  token,
  refreshKey,
  frameClassName,
}: ReportHtmlPreviewProps) {
  const [docHtml, setDocHtml] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      setFetchError(false);

      if (htmlFile) {
        setLoading(true);
        try {
          const text = await htmlFile.text();
          if (!cancelled) {
            setDocHtml(text);
          }
        } catch {
          if (!cancelled) {
            setDocHtml(null);
            setFetchError(true);
          }
        } finally {
          if (!cancelled) setLoading(false);
        }
        return;
      }

      setDocHtml(null);
      if (!serverReportId || !token) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const res = await fetchProjectAnalysisReportHtml(serverReportId, token);
        if (cancelled) return;
        if (res.ok) {
          setDocHtml(res.html);
        } else if (res.kind === 'not_found') {
          setDocHtml(null);
        } else {
          setFetchError(true);
          setDocHtml(null);
        }
      } catch {
        if (!cancelled) setFetchError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void run();
    return () => {
      cancelled = true;
    };
  }, [htmlFile, serverReportId, token, refreshKey]);

  if (loading) {
    return (
      <div className="flex min-h-[320px] items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-sm text-slate-500">
        Carregando pré-visualização…
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="flex min-h-[320px] items-center justify-center rounded-lg border border-red-200 bg-red-50 p-4 text-center text-sm text-red-800">
        Não foi possível carregar o HTML para pré-visualização.
      </div>
    );
  }

  if (!docHtml) {
    return (
      <div className="flex min-h-[320px] items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50 p-4 text-center text-sm text-slate-500">
        Envie um arquivo HTML ou salve o relatório para ver a pré-visualização aqui.
      </div>
    );
  }

  return (
    <iframe
      key={`${serverReportId ?? 'local'}-${refreshKey}-${htmlFile?.name ?? 'server'}`}
      title="Pré-visualização do relatório"
      className={cn(
        'w-full rounded-lg border border-slate-200 bg-white',
        frameClassName ?? 'h-[min(70vh,720px)]'
      )}
      srcDoc={docHtml}
      sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
    />
  );
}
